import React, { useState } from 'react'
import { ExpandableListItem } from '@fs/zion-ui'
import { useTranslation } from 'react-i18next'
import { useSlsStateContext } from '@fs/zion-sls'
import { useSharedBrowserStateContext } from '../../../../../shared/providers/sharedBrowserProvider/SharedBrowserContext'
import PersonDisplay from './PersonDisplay'
import { sortPersonsByPrimary } from '../utils/display.utils'

/**
 * Builds the URL that opens the image a person on another image lives on, in a new tab.
 *
 * @param {Object} person - The person element that lives on another image.
 * @returns {string} The absolute path to the person's image in index view.
 */
export const getPersonImageUrl = (person) =>
  `/ark:/61903/${person.partitionKey}?view=index&personArk=/ark:/61903/${person.id}&action=view`

/**
 * NamesOnOtherImages
 *
 * Renders a collapsible "Names on other images" section inside a group, below its
 * same-image members. It is shown whenever the group has members that live on a different
 * image than the current one. The count of those names is shown up front via a badge; expanding
 * the group reveals each name with an "On image [N]" overline and an open-in-new-tab action.
 *
 * @component
 * @param {Object} props
 * @param {Object} props.household - The household/record element to render other-image names for.
 * @param {Function} [props.onPersonSelect] - Reserved for parity with the members list; the rows here
 *   open the image in a new tab rather than selecting.
 * @returns {JSX.Element|null} The collapsible group, or null when there are no names on other images.
 */
const NamesOnOtherImages = ({ household }) => {
  const [t] = useTranslation()
  const { currentImageArk } = useSlsStateContext()
  const { thumbnails } = useSharedBrowserStateContext()
  const [open, setOpen] = useState(false)

  const subElements = household?.subElements ?? []
  // Persons in this household that live on a different image than the current one.
  const otherImagePersons = subElements.filter(
    (sub) => sub?.elementType === 'PERSON' && sub?.partitionKey !== currentImageArk
  )
  const sortedOtherImagePersons = sortPersonsByPrimary(otherImagePersons)

  const getImageNumber = (person) => {
    const index = thumbnails?.findIndex((thumbnail) => thumbnail.ark === person?.partitionKey) ?? -1
    return index >= 0 ? index + 1 : undefined
  }

  const handleToggle = () => setOpen((previouslyOpen) => !previouslyOpen)

  if (otherImagePersons.length === 0) return null

  return (
    <ExpandableListItem
      open={open}
      setOpen={handleToggle}
      emphasized
      badgeCount={otherImagePersons.length}
      primaryText={t('names-on-other-images.heading', 'Names on other images')}
      secondaryText={t('names-on-other-images.group-sub-heading', 'This group spans more than one image')}
      data-testid="names-on-other-images"
    >
      {sortedOtherImagePersons.map((person) => {
        const imageNumber = getImageNumber(person)

        return (
          <PersonDisplay
            key={person.id}
            person={person}
            record={household}
            overline={
              imageNumber !== undefined
                ? t('names-on-other-images.on-image', 'On image {imageNumber}', { imageNumber })
                : undefined
            }
            openNewPage={() => window.open(getPersonImageUrl(person), '_blank', 'noopener')}
          />
        )
      })}
    </ExpandableListItem>
  )
}

export default NamesOnOtherImages
